import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Sparkles, Camera, Sun, Image as ImageIcon, Footprints } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";

import goodFraming from "@/assets/tips/good-framing.webp";
import badFraming from "@/assets/tips/bad-framing.webp";
import goodLighting from "@/assets/tips/good-lighting.webp";
import badLighting from "@/assets/tips/bad-lighting.webp";

import badBackground from "@/assets/tips/bad-background.webp";
import goodBackgroundCloseup from "@/assets/tips/good-background-closeup.webp";
import goodShoes from "@/assets/tips/good-shoes.webp";
import badShoes from "@/assets/tips/bad-shoes.webp";

type Section = {
  icon: typeof Camera;
  eyebrow: string;
  title: string;
  description: string;
  good: { src: string; caption: string; points: string[] };
  bad: { src: string; caption: string; points: string[] };
  extra?: { src: string; caption: string; points: string[] };
};

const SECTIONS: Section[] = [
  {
    icon: Camera,
    eyebrow: "01 · Framing",
    title: "Show your full body",
    description:
      "We match clothes to your proportions. Stand straight, face the camera, and keep your whole body in frame — head to shoes.",
    good: {
      src: goodFraming,
      caption: "Full body, centered, facing camera",
      points: [
        "Head to feet visible",
        "Arms relaxed at sides",
        "Straight, centered pose",
      ],
    },
    bad: {
      src: badFraming,
      caption: "Cropped, tilted, face only",
      points: [
        "Body cut off or missing",
        "Tilted or angled shots",
        "Too close, only face visible",
      ],
    },
  },
  {
    icon: Sun,
    eyebrow: "02 · Lighting",
    title: "Bright & even light",
    description:
      "Natural daylight near a window is ideal. Avoid strong shadows on your face or backlight that turns you into a silhouette.",
    good: {
      src: goodLighting,
      caption: "Soft, even daylight on the face",
      points: [
        "Face clearly visible",
        "No harsh shadows",
        "Daylight or soft lamp in front",
      ],
    },
    bad: {
      src: badLighting,
      caption: "Dark room, backlit silhouette",
      points: [
        "Face in shadow",
        "Strong light behind you",
        "Dim, low-light rooms",
      ],
    },
  },
  {
    icon: ImageIcon,
    eyebrow: "03 · Background & Outfit",
    title: "Keep it simple",
    description:
      "A plain wall gives the best results. Skip sunglasses and hats — the AI needs to see your face clearly to get the look right.",
    good: {
      src: goodBackgroundCloseup,
      caption: "Close-up: Face & shoulders visible",
      points: [
        "Clear facial features",
        "Good for face-focused outfits",
        "Still shows some body context",
      ],
    },
    bad: {
      src: badBackground,
      caption: "Sunglasses, hat, busy scene",
      points: [
        "Hats or sunglasses covering face",
        "Cluttered or busy backdrop",
        "Very loose, oversized clothing",
      ],
    },
  },
  {
    icon: Footprints,
    eyebrow: "04 · Footwear",
    title: "Show your shoes",
    description:
      "For full-outfit and shoe try-ons, your feet need to be fully visible. Stand on a flat surface with both shoes in frame — no cropping at the ankles.",
    good: {
      src: goodShoes,
      caption: "Both shoes fully visible on flat ground",
      points: [
        "Feet flat, both shoes in frame",
        "Plain floor (tile, wood, mat)",
        "Standing upright, even weight",
      ],
    },
    bad: {
      src: badShoes,
      caption: "Feet cropped, angled, or hidden",
      points: [
        "Shoes cut off at the bottom",
        "One foot tucked behind the other",
        "Feet hidden by long pants or props",
      ],
    },
  },
];

const ExampleCard = ({
  kind,
  src,
  caption,
  points,
}: {
  kind: "good" | "bad";
  src: string;
  caption: string;
  points: string[];
}) => {
  const isGood = kind === "good";
  const Icon = isGood ? Check : X;
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-card shadow-soft",
        isGood ? "border-primary/40" : "border-destructive/40",
      )}
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
        <img
          src={src}
          alt={caption}
          loading="lazy"
          width={640}
          height={896}
          className="h-full w-full object-cover"
        />
        <div
          className={cn(
            "absolute left-2 top-2 flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md",
            isGood
              ? "bg-primary/90 text-primary-foreground"
              : "bg-destructive/90 text-destructive-foreground",
          )}
        >
          <Icon className="h-3 w-3" strokeWidth={3} />
          {isGood ? "Do" : "Don't"}
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs font-semibold text-foreground">{caption}</p>
        <ul className="mt-2 space-y-1">
          {points.map((p) => (
            <li
              key={p}
              className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
            >
              <Icon
                className={cn(
                  "mt-0.5 h-3 w-3 shrink-0",
                  isGood ? "text-primary" : "text-destructive",
                )}
                strokeWidth={3}
              />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const PhotoTips = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // SEO handled by <SEO /> component below.

  const inner = (
    <div className="mx-auto w-full max-w-md px-5 pb-10 pt-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="-ml-2 mb-4 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <header className="mb-6">
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          — PHOTO TIPS
        </span>
        <h1 className="font-display mt-1 flex items-center gap-2 text-2xl font-bold uppercase tracking-tight">
          <Sparkles className="h-5 w-5 text-primary" />
          Get the best try-on
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A great input photo = a great result. Here's what works and what doesn't,
          with real examples.
        </p>
      </header>

      <div className="space-y-8">
        {SECTIONS.map(({ icon: Icon, eyebrow, title, description, good, bad, extra }) => (
          <section key={eyebrow}>
            <div className="mb-3">
              <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary/80">
                {eyebrow}
              </span>
              <h2 className="font-display mt-1 flex items-center gap-2 text-lg font-bold uppercase tracking-tight text-foreground">
                <Icon className="h-4 w-4 text-primary" />
                {title}
              </h2>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {description}
              </p>
            </div>

            <div className={cn("grid gap-3", extra ? "grid-cols-3" : "grid-cols-2")}>
              <ExampleCard kind="good" {...good} />
              <ExampleCard kind="bad" {...bad} />
              {extra && <ExampleCard kind="good" {...extra} />}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-5 text-center shadow-elevated">
        <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          Ready?
        </p>
        <h3 className="font-display mt-1 text-lg font-bold uppercase tracking-tight">
          Take your best shot
        </h3>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Follow these tips and your try-on will look incredible.
        </p>
        <Button asChild className="mt-4 w-full">
          <Link to="/app">
            <Sparkles className="mr-2 h-4 w-4" /> Start try-on
          </Link>
        </Button>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-6" />
      </div>
    </div>
  );

  return (
    <>
      <SEO />
      {isMobile ? <MobileShell title="Photo Tips">{inner}</MobileShell> : inner}
    </>
  );
};

export default PhotoTips;
